import React, { useEffect, useState } from "react";
import { Calendar, momentLocalizer } from "react-big-calendar";
import moment from "moment";
import "react-big-calendar/lib/css/react-big-calendar.css";
import { useDispatch, useSelector } from "react-redux";
import { fetchTasks } from "../../../state/reducers/dashboardSlice";
import { fetchReminders } from "../../../state/reducers/reminderSlice";

// Localize moment
const localizer = momentLocalizer(moment);

// Custom Event Component with improved styling
function CustomEvent({ event }) {
  const priorityColors = {
    High: "bg-red-100 text-red-700 border-red-200",
    Medium: "bg-yellow-100 text-yellow-700 border-yellow-200",
    Low: "bg-green-100 text-green-700 border-green-200",
  };

  const typeColors = {
    Task: "bg-blue-100 text-blue-700 border-blue-200",
    Reminder: "bg-purple-100 text-purple-700 border-purple-200",
  };

  const badgeClass =
    event.type === "Task" && event.priority
      ? priorityColors[event.priority]
      : typeColors[event.type] || "bg-gray-100 text-gray-700 border-gray-200";

  return (
    <div className="px-1 py-0.5">
      <div className="flex items-center mb-0.5">
        <span
          className={`text-xs font-medium px-1.5 py-0.5 rounded ${badgeClass}`}
        >
          {event.type}
        </span>
      </div>
      <div className="font-medium text-xs truncate">{event.title}</div>
      {event.type !== "Task" && event.start && (
        <div className="text-xs opacity-75 mt-0.5">
          {moment(event.start).format("h:mm A")}
        </div>
      )}
    </div>
  );
}

function Cal({ reminderFromNotification }) {
  const dispatch = useDispatch();
  const [calendarDate, setCalendarDate] = useState(new Date());

  // Access tasks and reminders from Redux
  const {
    tasks,
    isLoading: tasksLoading,
    error: tasksError,
  } = useSelector((state) => state.dashboard);
  const {
    reminders,
    isLoading: remindersLoading,
    error: remindersError,
  } = useSelector((state) => state.reminders);

  // Local state for selected event & modal
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [view, setView] = useState("month");

  // Fetch tasks and reminders on mount
  useEffect(() => {
    dispatch(fetchReminders());
    dispatch(fetchTasks());
  }, [dispatch]);

  useEffect(() => {
    if (remindersLoading) return; // Wait until loading is done
    if (!reminderFromNotification || reminders.length === 0) return;
    console.log(
      "Processing reminder from notification:",
      reminderFromNotification
    );
    const strippedMessage = reminderFromNotification.message
      ?.replace(/^Reminder:\s*/i, "")
      .trim();

    const matchedReminder = reminders.find(
      (r) => r.description?.trim() === strippedMessage
    );

    if (matchedReminder) {
      const reminderDateTime = moment(
        `${matchedReminder.trigger_date} ${matchedReminder.trigger_time}`,
        "YYYY-MM-DD HH:mm:ss"
      ).toDate();

      const pseudoEvent = {
        id: matchedReminder.id,
        title: matchedReminder.description || "Reminder",
        start: reminderDateTime,
        end: reminderDateTime,
        description: matchedReminder.description,
        type: "Reminder",
        trigger_time: matchedReminder.trigger_time,
      };

      // 🔥 New: set calendar to show the correct month/date
      setCalendarDate(reminderDateTime); // 👈 this makes the calendar go to the correct month
      handleSelectEvent(pseudoEvent);
    } else {
      console.log("Reminder not matched:", strippedMessage);
    }
  }, [reminderFromNotification, reminders, remindersLoading]);

  // Combine tasks and reminders into a single array for the calendar
  const events = [
    ...tasks.map((task) => ({
      id: task.id,
      title: task.title,
      start: moment(task.start_date, "YYYY-MM-DD HH:mm:ss").toDate(),
      end: moment(
        task.due_date || task.start_date,
        "YYYY-MM-DD HH:mm:ss"
      ).toDate(),
      priority: task.priority,
      description: task.description,
      type: "Task",
    })),
    ...reminders.map((reminder) => {
      const reminderDateTime = moment(
        `${reminder.trigger_date} ${reminder.trigger_time}`,
        "YYYY-MM-DD HH:mm:ss"
      ).toDate();

      return {
        id: reminder.id,
        title: reminder.description || "Reminder",
        start: reminderDateTime,
        end: reminderDateTime,
        description: reminder.description,
        type: "Reminder",
        trigger_time: reminder.trigger_time,
      };
    }),
  ];

  // Group reminders and tasks by date
  const remindersByDate = reminders.reduce((acc, reminder) => {
    const reminderDate = moment(reminder.trigger_date).format("YYYY-MM-DD");
    if (!acc[reminderDate]) acc[reminderDate] = [];
    acc[reminderDate].push(reminder);
    return acc;
  }, {});

  const tasksByDate = tasks.reduce((acc, task) => {
    const taskDate = moment(task.start_date).format("YYYY-MM-DD");
    if (!acc[taskDate]) acc[taskDate] = [];
    acc[taskDate].push(task);
    return acc;
  }, {});

  // Color-code events by type and priority
  const eventStyleGetter = (event) => {
    let backgroundColor = "#4F46E5"; // Indigo for default tasks

    if (event.type === "Reminder") {
      backgroundColor = "#8B5CF6"; // Purple for reminders
    } else if (event.priority === "High") {
      backgroundColor = "#EF4444"; // Red for high-priority tasks
    } else if (event.priority === "Medium") {
      backgroundColor = "#F59E0B"; // Amber for medium-priority tasks
    } else if (event.priority === "Low") {
      backgroundColor = "#10B981"; // Emerald for low-priority tasks
    }

    return {
      style: {
        backgroundColor,
        borderRadius: "6px",
        opacity: 0.85,
        color: "white",
        border: "0px",
        display: "block",
      },
    };
  };

  // Handle event click
  const handleSelectEvent = (event) => {
    setSelectedEvent(event);
    const selectedDate = moment(event.start).format("YYYY-MM-DD");

    // Get tasks and reminders for the selected date
    const selectedReminders = remindersByDate[selectedDate] || [];
    const selectedTasks = tasksByDate[selectedDate] || [];

    setSelectedEvent({
      ...event,
      reminders: selectedReminders,
      tasks: selectedTasks,
    });
    window.scrollTo({
      top: document.body.scrollHeight,
      behavior: "smooth",
    });
    setShowModal(true);
  };

  // Show loading/error states
  if (tasksLoading || remindersLoading) {
    return (
      <div className="flex items-center justify-center h-96 bg-white rounded-2xl">
        <div className="flex flex-col items-center">
          <svg
            className="animate-spin h-10 w-10 text-purple-600 mb-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            ></circle>
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            ></path>
          </svg>
          <p className="text-slate-600">Loading calendar data...</p>
        </div>
      </div>
    );
  }

  if (tasksError || remindersError) {
    return (
      <div className="flex items-center justify-center h-96 bg-white rounded-2xl">
        <div className="bg-rose-100 border-l-4 border-rose-500 text-rose-700 p-4 rounded-xl">
          <p className="font-medium">Error loading calendar data</p>
          <p className="text-sm">{tasksError || remindersError}</p>
        </div>
      </div>
    );
  }

  // Custom toolbar component
  const CustomToolbar = (toolbar) => {
    const goToToday = () => {
      toolbar.date.setMonth(new Date().getMonth());
      toolbar.date.setYear(new Date().getFullYear());
      toolbar.onNavigate("TODAY");
    };

    const goToPrev = () => {
      toolbar.onNavigate("PREV");
    };

    const goToNext = () => {
      toolbar.onNavigate("NEXT");
    };

    const label = () => {
      const date = moment(toolbar.date);
      return (
        <span className="text-lg font-semibold text-slate-800">
          {date.format(view === "month" ? "MMMM YYYY" : "MMM D, YYYY")}
        </span>
      );
    };

    const changeView = (newView) => {
      setView(newView);
      toolbar.onView(newView);
    };

    return (
      <div className="flex flex-col sm:flex-row sm:justify-between items-center mb-5 px-2">
        <div className="flex items-center mb-3 sm:mb-0">
          <button
            type="button"
            onClick={goToPrev}
            className="p-2 mr-2 rounded-full hover:bg-slate-100"
          >
            <svg
              className="h-5 w-5 text-slate-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <button
            type="button"
            onClick={goToToday}
            className="px-3 py-1.5 text-sm font-medium bg-indigo-100 text-indigo-700 rounded-lg mr-2 hover:bg-indigo-200"
          >
            Today
          </button>

          <button
            type="button"
            onClick={goToNext}
            className="p-2 rounded-full hover:bg-slate-100"
          >
            <svg
              className="h-5 w-5 text-slate-600"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          </button>

          <div className="ml-4">{label()}</div>
        </div>

        <div className="flex bg-slate-100 rounded-xl p-1">
          <button
            type="button"
            onClick={() => changeView("month")}
            className={`px-3 py-1.5 text-sm font-medium rounded-md ${
              view === "month"
                ? "bg-white shadow-sm text-indigo-700"
                : "text-slate-600 hover:bg-slate-200"
            }`}
          >
            Month
          </button>
          <button
            type="button"
            onClick={() => changeView("week")}
            className={`px-3 py-1.5 text-sm font-medium rounded-md ${
              view === "week"
                ? "bg-white shadow-sm text-indigo-700"
                : "text-slate-600 hover:bg-slate-200"
            }`}
          >
            Week
          </button>
          <button
            type="button"
            onClick={() => changeView("day")}
            className={`px-3 py-1.5 text-sm font-medium rounded-md ${
              view === "day"
                ? "bg-white shadow-sm text-indigo-700"
                : "text-slate-600 hover:bg-slate-200"
            }`}
          >
            Day
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm">
      <div className="p-4 border-b border-slate-200/80">
        <div className="flex items-center">
          <div className="flex items-center justify-center w-8 h-8 rounded-full bg-indigo-100 text-indigo-600 mr-3">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <h2 className="text-lg font-semibold text-slate-800">Calendar</h2>
        </div>
      </div>

      <div className="p-4">
        <div className="flex flex-wrap space-x-2 mb-4 items-center">
          <span className="text-sm text-slate-600 mr-2">Legend:</span>
          <div className="flex items-center">
            <span className="inline-block w-3 h-3 rounded-full bg-red-500 mr-1"></span>
            <span className="text-xs text-slate-600">High Priority</span>
          </div>
          <div className="flex items-center">
            <span className="inline-block w-3 h-3 rounded-full bg-yellow-500 mr-1"></span>
            <span className="text-xs text-slate-600">Medium Priority</span>
          </div>
          <div className="flex items-center">
            <span className="inline-block w-3 h-3 rounded-full bg-green-500 mr-1"></span>
            <span className="text-xs text-slate-600">Low Priority</span>
          </div>
          <div className="flex items-center">
            <span className="inline-block w-3 h-3 rounded-full bg-purple-500 mr-1"></span>
            <span className="text-xs text-slate-600">Reminders</span>
          </div>
        </div>

        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          style={{ height: 550 }}
          eventPropGetter={eventStyleGetter}
          components={{
            event: CustomEvent,
            toolbar: CustomToolbar,
          }}
          onSelectEvent={handleSelectEvent}
          view={view}
          onView={setView}
          views={["month", "week", "day"]}
          date={calendarDate} // 👈 Add this
          onNavigate={(newDate) => setCalendarDate(newDate)} // 👈 Update on navigation
        />
      </div>

      {/* Enhanced Modal for Event Details */}
      {showModal && selectedEvent && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-30 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 overflow-hidden">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-violet-600 to-indigo-600 text-white px-6 py-4">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-bold">Event Details</h2>
                <button
                  className="text-white hover:bg-purple-700 rounded-full p-1"
                  onClick={() => setShowModal(false)}
                >
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="p-6">
              <div className="mb-4">
                <div className="flex items-center mb-3">
                  <div
                    className={`px-2 py-1 rounded-md text-sm font-medium ${
                      selectedEvent.type === "Task"
                        ? "bg-blue-100 text-blue-700"
                        : "bg-indigo-100 text-indigo-700"
                    }`}
                  >
                    {selectedEvent.type}
                  </div>
                  <div className="text-slate-400 text-sm ml-3">
                    {moment(selectedEvent.start).format("dddd, MMMM D, YYYY")}
                  </div>
                </div>

                {selectedEvent.type === "Task" && (
                  <div
                    className={`px-2 py-1 inline-block rounded-md text-xs font-medium mb-3 ${
                      selectedEvent.priority === "High"
                        ? "bg-red-100 text-red-700"
                        : selectedEvent.priority === "Medium"
                        ? "bg-yellow-100 text-yellow-700"
                        : "bg-green-100 text-green-700"
                    }`}
                  >
                    {selectedEvent.priority} Priority
                  </div>
                )}
              </div>

              {/* Event Details */}
              <div className="overflow-y-auto max-h-96 pr-2">
                {/* Selected Event Tasks */}
                {selectedEvent.tasks && selectedEvent.tasks.length > 0 && (
                  <div className="mb-6">
                    <h3 className="font-semibold text-slate-800 mb-3 flex items-center">
                      <svg
                        className="w-5 h-5 mr-2 text-purple-600"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
                        />
                      </svg>
                      Tasks
                    </h3>
                    <div className="space-y-3">
                      {selectedEvent.tasks.map((task) => (
                        <div
                          key={task.id}
                          className="p-4 border border-slate-200 rounded-xl hover:border-indigo-200 transition-colors"
                        >
                          <h4 className="font-medium text-slate-800 mb-2">
                            {task.title}
                          </h4>

                          <div className="flex items-center mb-2">
                            <span
                              className={`px-2 py-0.5 rounded text-xs font-medium ${
                                task.priority === "High"
                                  ? "bg-red-100 text-red-700"
                                  : task.priority === "Medium"
                                  ? "bg-yellow-100 text-yellow-700"
                                  : "bg-green-100 text-green-700"
                              }`}
                            >
                              {task.priority} Priority
                            </span>

                            <span className="text-slate-500 text-xs ml-2">
                              Due:{" "}
                              {moment(task.due_date || task.start_date).format(
                                "MMM D, YYYY"
                              )}
                            </span>
                          </div>

                          {task.description && (
                            <p className="text-sm text-slate-600 mt-2">
                              {task.description}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Selected Event Reminders */}
                {selectedEvent.reminders &&
                  selectedEvent.reminders.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-slate-800 mb-3 flex items-center">
                        <svg
                          className="w-5 h-5 mr-2 text-purple-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"
                          />
                        </svg>
                        Reminders
                      </h3>
                      <div className="space-y-3">
                        {selectedEvent.reminders.map((reminder) => (
                          <div
                            key={reminder.id}
                            className="p-4 border border-slate-200 rounded-xl hover:border-indigo-200 transition-colors"
                          >
                            <p className="font-medium text-slate-800 mb-2">
                              {reminder.description}
                            </p>
                            <div className="flex items-center text-sm text-slate-600">
                              <svg
                                className="w-4 h-4 mr-1 text-purple-500"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                                />
                              </svg>
                              {moment(
                                `${reminder.trigger_date} ${reminder.trigger_time}`,
                                "YYYY-MM-DD HH:mm:ss"
                              ).format("h:mm A")}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-slate-50 px-6 py-3 flex justify-end border-t border-slate-200/80">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Cal;
